<!-- src/pages/employees/index.vue -->
<template>
  <section class="page-header mb-3">
    <div class="container d-flex justify-content-between align-items-end">
      <div class="page-header-title">
        <h1 class="h3 mb-1">
          <i class="fa-solid fa-layer-group"></i>
          {{ $t('page.employees.index_title') }}
        </h1>
        <p class="text-light opacity-75 mb-0">{{ $t('page.employees.index_description') }}</p>
      </div>
      <div class="page-header-actions">
        <button class="btn btn-sm btn-primary" @click.prevent="handleCreate()" :title="$t('common.add')">
          <i class="fa fa-plus"></i> {{ $t('common.new') }}
        </button>
        <button class="btn btn-sm btn-primary d-sm-none ml-1" @click="toggleFilters" :title="$t('common.filter')">
          <i class="fa-solid fa-filter"></i>
        </button>
      </div>
    </div>
  </section>

  <!-- Filters -->
  <section class="container mb-3">
    <div class="page-filters shadow-sm p-3 collapse d-md-block" :class="{ show: showFilters }" id="filtersCollapse">
      <div class="row g-3">
        <div class="col-md-5">
          <div class="input-group">
            <span class="input-group-text"><i class="fas fa-search"></i></span>
            <input type="text" class="form-control" v-model="search" :placeholder="$t('common.searchPlaceholder')" />
          </div>
        </div>
        <div class="col-md-3">
          <select class="form-select" v-model="priorityFilter">
            <option value="">{{ $t('common.all_priorities') }}</option>
            <option value="high">Alta</option>
            <option value="medium">Média</option>
            <option value="low">Baixa</option>
          </select>
        </div>
        <div class="col-md-2">
          <select class="form-select" v-model.number="perPage">
            <option :value="5">5 por página</option>
            <option :value="10">10 por página</option>
            <option :value="20">20 por página</option>
            <option :value="50">50 por página</option>
          </select>
        </div>
        <div class="col-md-2">
          <div class="input-group">
            <button class="btn btn-secondary w-50" @click="resetFilters">
              <i class="fas fa-eraser"></i>
            </button>
            <button class="btn btn-secondary w-50" @click="printTable">
              <i class="fa-solid fa-print"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>

  <div class="container">
    <!-- Loading -->
    <div v-if="employeeStore.loading" class="text-center py-5">
      <i class="fa fa-spinner fa-spin fa-2x text-primary"></i>
      <p>{{ $t('common.loading') }}</p>
    </div>

    <!-- Table -->
    <div v-else class="table-responsive shadow-sm rounded">
      <table class="table table-hover align-middle">
        <thead>
          <tr>
            <th @click="sortBy('id')" style="cursor:pointer">
              {{ $t('page.employees.inputs.id') }} <i :class="sortIcon('id')"></i>
            </th>
            <th @click="sortBy('name')" style="cursor:pointer">
              {{ $t('page.employees.inputs.name') }} <i :class="sortIcon('name')"></i>
            </th>
            <th @click="sortBy('version')" style="cursor:pointer">
              {{ $t('page.employees.inputs.version') }} <i :class="sortIcon('version')"></i>
            </th>
            <th class="text-end"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="employee in paginatedEmployees" :key="employee.id">
            <td>{{ employee.id }}</td>
            <td>{{ employee.name }}</td>
            <td>{{ employee.version }}</td>
            <td class="text-end">
              <div class="btn-group btn-group-sm text-center">
                <button class="btn btn-sm btn-secondary" @click="handleView(employee)" :title="$t('common.view')">
                  <i class="fas fa-eye"></i>
                </button>
                <button class="btn btn-sm btn-secondary" @click="handleEdit(employee)" :title="$t('common.edit')">
                  <i class="fas fa-pencil-alt"></i>
                </button>
                <button class="btn btn-sm btn-danger" @click="handleDelete(employee)" :title="$t('common.delete')">
                  <i class="far fa-trash-alt"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="!paginatedEmployees.length">
            <td colspan="4" class="text-center text-muted">{{ $t('common.no_items_found') }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <nav v-if="pages > 1">
      <ul class="pagination justify-content-center">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <button class="page-link" @click="currentPage--">&laquo;</button>
        </li>
        <li v-for="n in pages" :key="n" class="page-item" :class="{ active: n === currentPage }">
          <button class="page-link" @click="currentPage = n">{{ n }}</button>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === pages }">
          <button class="page-link" @click="currentPage++">&raquo;</button>
        </li>
      </ul>
    </nav>

    <!-- Modal Delete -->
    <div v-if="confirming" class="modal fade show d-block" tabindex="-1" style="background: rgba(0,0,0,0.5)"
      @click.self="confirming = false">
      <div class="modal-dialog modal-md">
        <div class="modal-content">
          <div class="modal-header">
            <h6 class="modal-title">{{ $t('common.delete_confirm') }}</h6>
            <button type="button" class="btn-close" @click="confirming = false"></button>
          </div>
          <div class="modal-body">
            <p>{{ $t('common.delete_selected_confirm') }}</p>
            <div><strong>{{ $t('page.employees.inputs.id') }}:</strong> {{ toDelete?.id }}</div>
            <div><strong>{{ $t('page.employees.inputs.name') }}:</strong> {{ toDelete?.name }}</div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary btn-sm" @click="confirming = false">{{ $t('common.cancel') }}</button>
            <button class="btn btn-danger btn-sm" @click="deleteEmployee">{{ $t('common.delete') }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useEmployeeStore } from "@/stores/employeeStore";

// Consts
const router = useRouter();
const employeeStore = useEmployeeStore();

// States
const search = ref("");
const priorityFilter = ref("");
const sortKey = ref("id");
const sortAsc = ref(true);
const perPage = ref(5);
const currentPage = ref(1);
const showFilters = ref(false);
const confirming = ref(false);
const toDelete = ref(null);

// Load employees
const loadEmployees = async () => {
  employeeStore.loading = true;
  try {
    await employeeStore.getAllEmployees();
  } finally {
    employeeStore.loading = false;
  }
};

// Sorting
const sortBy = (key) => {
  if (sortKey.value === key) sortAsc.value = !sortAsc.value;
  else {
    sortKey.value = key;
    sortAsc.value = true;
  }
};

const sortIcon = (key) => {
  if (sortKey.value !== key) return "fa fa-sort text-muted";
  return sortAsc.value ? "fa fa-sort-up" : "fa fa-sort-down";
};

// CRUD Handlers
const handleCreate = () => router.push({ name: "employees-create" });
const handleView = (employee) => router.push({ name: "employees-show", params: { id: employee.id } });
const handleEdit = (employee) => router.push({ name: "employees-edit", params: { id: employee.id } });
const handleDelete = (employee) => {
  toDelete.value = employee;
  confirming.value = true;
};

const deleteEmployee = async () => {
  if (!toDelete.value) return;
  try {
    await employeeStore.deleteEmployee(toDelete.value.id);
    loadEmployees();
  } catch (error) {
    alert("Erro ao deletar o projeto!");
  } finally {
    confirming.value = false;
  }
};

// Filters + sort + pagination
const filtered = computed(() => {
  return employeeStore.employees
    .filter(p => p.name.toLowerCase().includes(search.value.toLowerCase()))
    .filter(p => !priorityFilter.value || p.priority === priorityFilter.value)
    .sort((a, b) => {
      const valA = a[sortKey.value];
      const valB = b[sortKey.value];
      return sortAsc.value ? (valA > valB ? 1 : -1) : (valA < valB ? 1 : -1);
    });
});

const toggleFilters = () => {
  showFilters.value = !showFilters.value;
};

const pages = computed(() => Math.max(Math.ceil(filtered.value.length / perPage.value), 1));

const paginatedEmployees = computed(() => {
  const start = (currentPage.value - 1) * perPage.value;
  return filtered.value.slice(start, start + perPage.value);
});

// Filters actions
const resetFilters = () => {
  search.value = "";
  priorityFilter.value = "";
  perPage.value = 5;
  currentPage.value = 1;
};

// Placeholder for print
const printTable = () => {
  window.print();
};

// Mounted
onMounted(loadEmployees);
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #2c3e50, #4a6580);
  color: white;
  padding: 20px 0;
}

.page-filters {
  background: white;
  border-radius: 12px;
}

.fa-spin {
  margin: 10px 0;
}
</style>
